'use client';

import { useEffect, type MutableRefObject } from 'react';
import { UNAUTHORIZED_EVENT, type UnauthorizedEventDetail } from '@/lib/utils/api-auth';
import {
  buildAuthReturnTo,
  shouldIgnoreUnauthorized,
  UNAUTHORIZED_IGNORED_PATHNAMES,
  VISIBILITY_REFRESH_STALE_MS,
} from '@/contexts/AuthContext.helpers';
import type { AuthUser } from '@/contexts/AuthContext';

interface UseSessionTimeoutParams {
  /** The resolved auth user — the 401 handler no-ops when there is nobody to sign out. */
  user: AuthUser | null;
  /** Clears local auth state (provider's `applyState`). */
  applyState: (nextUser: AuthUser | null, nextAuthRequired?: boolean) => void;
  /** Clears any stored pending 2FA challenge (provider's callback). */
  clearPendingTwoFactorChallenge: () => void;
  /** Re-reads /api/auth/me; called silently on visibility return. */
  refresh: (options?: { silent?: boolean }) => Promise<void>;
  /** Provider-owned mounted flag — guards the async event handlers. */
  mountedRef: MutableRefObject<boolean>;
}

/**
 * `useSessionTimeout` — the two session-lifecycle listeners the auth provider
 * installs: the global 401 handler and the visibility-return revalidation.
 *
 * Extracted verbatim from `AuthContext` (Phase 1.6 (7)). Both effect bodies —
 * Fix 1 (UNAUTHORIZED_EVENT → clear pending 2FA + applyState(null) +
 * return_to redirect, gated by shouldIgnoreUnauthorized / !user /
 * UNAUTHORIZED_IGNORED_PATHNAMES) and Fix 2 (visibilitychange → silent refresh
 * past VISIBILITY_REFRESH_STALE_MS) — are byte-for-byte identical to the
 * originals. The provider-scoped values (user, applyState,
 * clearPendingTwoFactorChallenge, refresh, mountedRef) are injected as params.
 *
 * The 401 effect's dep array gains `mountedRef` (a stable injected ref): it is a
 * no-op for re-subscription cadence — the security-critical re-subscription
 * triggers stay exactly [user, applyState, clearPendingTwoFactorChallenge] — but
 * it satisfies exhaustive-deps now that mountedRef is a param rather than an
 * in-scope `useRef` the rule recognizes as stable.
 */
export function useSessionTimeout({
  user,
  applyState,
  clearPendingTwoFactorChallenge,
  refresh,
  mountedRef,
}: UseSessionTimeoutParams): void {
  // Fix 1: global 401 handler — when any authenticated request 401s, clear
  // local user state and send the user to the auth flow with a return_to.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onUnauthorized = (event: Event) => {
      if (!mountedRef.current) return;
      const detail = (event as CustomEvent<UnauthorizedEventDetail>).detail;

      // Skip expected auth-flow 401s (login failures, /auth/me probe, 2FA, etc).
      if (shouldIgnoreUnauthorized(detail)) return;

      // If we already have no user, there's nothing to log out — avoid extra
      // navigations or refresh churn.
      if (!user) return;

      console.warn('[auth] Session expired (401) — signing out', detail?.url ?? '(unknown url)');

      // Clear local state first so subsequent guarded UI renders the auth gate
      // instead of firing more API calls that'd 401 again.
      clearPendingTwoFactorChallenge();
      applyState(null);

      // Redirect to the auth entry with a return_to, matching the existing
      // convention used elsewhere in the app. Use window.location so this
      // works regardless of the surrounding router context and survives
      // non-Next.js pages (e.g., proxy-served previews).
      try {
        const returnTo = buildAuthReturnTo();
        const target = `/auth?return_to=${encodeURIComponent(returnTo)}`;
        // Only navigate if we're not already on an auth-related route.
        if (!UNAUTHORIZED_IGNORED_PATHNAMES.some((p) => window.location.pathname.includes(p))) {
          window.location.assign(target);
        }
      } catch {
        // Best-effort redirect; state is already cleared.
      }
    };

    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    // mountedRef is a stable injected ref (see the hook JSDoc); the real
    // re-subscription triggers are [user, applyState, clearPendingTwoFactorChallenge].
  }, [user, applyState, clearPendingTwoFactorChallenge, mountedRef]);

  // Fix 2: when the tab regains visibility after being hidden long enough that
  // the session may have rotated server-side, re-check /api/auth/me so the UI
  // reflects reality instead of stale "logged in" state.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    let lastRefresh = Date.now();

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefresh < VISIBILITY_REFRESH_STALE_MS) return;
      lastRefresh = Date.now();
      // Silent: revalidate the session without flipping isLoading, so the
      // auth mount-gates don't unmount the authed tree (and the preview
      // iframe) on every tab-return. An expired session still surfaces via
      // applyState(null) → user=null → gate redirects to /auth.
      refresh({ silent: true }).catch(() => {
        /* best-effort; errors are already surfaced via user=null */
      });
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [refresh]);
}
